/**
 * TasksHub — MS-ToDo-style 3-column task UI integrated in Terminplaner.
 *
 * Desktop: [Sidebar | List | Detail]
 * Mobile:  Single-Screen, toggling Liste <-> Detail
 *
 * Farben & Design bleiben TGAone (CSS-Variablen),
 * nur Struktur/UX entspricht MS ToDo.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { authFetch } from "../App";
import { toast } from "sonner";
import {
  Plus, Check, Trash2, X, Calendar as CalendarIcon, Briefcase,
  User as UserIcon, FolderKanban, ListChecks, ChevronLeft, Eye,
  Star, AlertOctagon, Loader2, Link2, Users, Inbox, Pencil, Tag,
} from "lucide-react";
import { format, parseISO, isToday, isTomorrow, isPast } from "date-fns";
import { de } from "date-fns/locale";

const API = "/api";

const PRIORITY_DOT = {
  niedrig: "#64748b",
  normal: "#60a5fa",
  hoch: "#f59e0b",
  kritisch: "#ef4444",
};

const VISIBILITY_OPTS = [
  { k: "privat", l: "Privat" },
  { k: "team", l: "Projektteam" },
  { k: "specific", l: "Bestimmte Mitarbeiter" },
  { k: "alle", l: "Alle" },
];

const FIXED_LISTS = [
  { id: "all", label: "Meine Aufgaben", icon: ListChecks, match: () => true },
];

const CATEGORY_COLOR_PALETTE = [
  "#3b82f6", "#f59e0b", "#8b5cf6", "#10b981", "#ef4444",
  "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#64748b",
];

function fmtDue(d) {
  if (!d) return "";
  try {
    const p = parseISO(d);
    if (isToday(p)) return "Heute";
    if (isTomorrow(p)) return "Morgen";
    return format(p, "EEE, dd. MMM", { locale: de });
  } catch { return d; }
}

function isOverdue(t) {
  if (!t.due_date || t.status === "erledigt") return false;
  try { return isPast(parseISO(t.due_date)) && !isToday(parseISO(t.due_date)); } catch { return false; }
}

export default function TasksHub({ projects = [], onLinkToEvent }) {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]); // dynamische User-Kategorien
  const [loading, setLoading] = useState(true);
  const [selectedListId, setSelectedListId] = useState("all");
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [quickAdd, setQuickAdd] = useState("");
  const [savingQuick, setSavingQuick] = useState(false);
  const [mobileView, setMobileView] = useState("list"); // list | detail
  const [manageMode, setManageMode] = useState(false); // Sidebar-Bearbeitungsmodus
  const [newCatName, setNewCatName] = useState("");
  const [newCatColor, setNewCatColor] = useState(CATEGORY_COLOR_PALETTE[0]);
  const [savingNewCat, setSavingNewCat] = useState(false);
  const meId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}")?.id || ""; } catch { return ""; }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/task-categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(Array.isArray(data.categories) ? data.categories : []);
      }
    } catch { /* silent */ }
  }, []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/my-tasks?include_done=true&group=false`);
      if (res.ok) {
        const data = await res.json();
        setTasks(Array.isArray(data.tasks) ? data.tasks : []);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadTasks(); loadCategories(); }, [loadTasks, loadCategories]);
  useEffect(() => {
    authFetch(`${API}/users/directory`).then(r => r.ok ? r.json() : []).then(setUsers).catch(() => {});
  }, []);

  // Dynamische Kategorie-Counts (Slug -> count)
  const counts = useMemo(() => {
    const c = { all: 0, bySlug: {}, byProject: {} };
    tasks.forEach(t => {
      if (t.status === "erledigt") return;
      c.all++;
      if (t.category) c.bySlug[t.category] = (c.bySlug[t.category] || 0) + 1;
      if (t.project_id) c.byProject[t.project_id] = (c.byProject[t.project_id] || 0) + 1;
    });
    return c;
  }, [tasks]);

  // Filtered list — filtert auf Slug oder spezielle Listen
  const filteredTasks = useMemo(() => {
    let list = tasks;
    if (!showCompleted) list = list.filter(t => t.status !== "erledigt");

    if (selectedListId === "all") {
      // alle behalten
    } else if (selectedListId.startsWith("cat:")) {
      const slug = selectedListId.slice(4);
      list = list.filter(t => t.category === slug);
    } else if (selectedListId.startsWith("project:")) {
      const pid = selectedListId.slice(8);
      list = list.filter(t => t.project_id === pid);
    } else {
      // Legacy fallback: Behandlung als category-Slug
      list = list.filter(t => t.category === selectedListId);
    }

    // Sort: overdue → today → upcoming → no date → completed (last)
    return [...list].sort((a, b) => {
      const aDone = a.status === "erledigt", bDone = b.status === "erledigt";
      if (aDone !== bDone) return aDone ? 1 : -1;
      const aOver = isOverdue(a), bOver = isOverdue(b);
      if (aOver !== bOver) return aOver ? -1 : 1;
      const aD = a.due_date || "9999-12-31";
      const bD = b.due_date || "9999-12-31";
      return aD.localeCompare(bD);
    });
  }, [tasks, selectedListId, showCompleted]);

  const selectedTask = useMemo(
    () => tasks.find(t => t.id === selectedTaskId) || null,
    [tasks, selectedTaskId]
  );

  // Auto-select first task on desktop when list changes (only on desktop)
  useEffect(() => {
    if (window.innerWidth < 768) return;
    if (!selectedTaskId && filteredTasks.length > 0) {
      setSelectedTaskId(filteredTasks[0].id);
    } else if (selectedTaskId && !filteredTasks.find(t => t.id === selectedTaskId)) {
      setSelectedTaskId(filteredTasks[0]?.id || null);
    }
  }, [filteredTasks, selectedTaskId]);

  // ---------- ACTIONS ----------
  const submitQuick = async () => {
    const title = quickAdd.trim();
    if (!title) return;
    setSavingQuick(true);
    try {
      // Default category derived from active list
      let category = null;
      let project_id = null;
      if (selectedListId === "all") {
        // First non-default category, else "buero"
        category = categories[0]?.slug || null;
      } else if (selectedListId.startsWith("cat:")) {
        category = selectedListId.slice(4);
      } else if (selectedListId.startsWith("project:")) {
        project_id = selectedListId.slice(8);
        category = "projekt";
      }
      const body = { title, category, project_id, priority: "normal", visibility: "privat" };
      const res = await authFetch(`${API}/personal-tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const created = await res.json();
        setQuickAdd("");
        // Optimistic insert
        setTasks(prev => [{ ...created, task_type: "PERSONAL" }, ...prev]);
        // background reload to ensure consistency
        loadTasks();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler beim Anlegen");
      }
    } catch { toast.error("Netzwerkfehler"); }
    setSavingQuick(false);
  };

  // Build endpoint based on task type (PROJECT tasks use /api/projects/{pid}/tasks/{tid},
  // PERSONAL use /api/personal-tasks/{tid}). Fixes "Das ist eine Projekt-Aufgabe" 400 error.
  const taskEndpoint = (task) => {
    const isProject = task && (task.task_type === "PROJECT" || task.project_id);
    return isProject
      ? `${API}/projects/${task.project_id}/tasks/${task.id}`
      : `${API}/personal-tasks/${task.id}`;
  };

  const toggleDone = async (task) => {
    const next = task.status === "erledigt" ? "offen" : "erledigt";
    // Optimistic UI
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: next } : t));
    try {
      const res = await authFetch(taskEndpoint(task), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) {
        // revert
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler");
      }
    } catch {
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
      toast.error("Netzwerkfehler");
    }
  };

  const updateTask = async (taskId, patch) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    // Optimistic
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...patch } : t));
    try {
      const res = await authFetch(taskEndpoint(task), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const updated = await res.json();
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updated } : t));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler beim Speichern");
        loadTasks();
      }
    } catch {
      toast.error("Netzwerkfehler");
      loadTasks();
    }
  };

  const deleteTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    if (!confirm("Aufgabe wirklich löschen?")) return;
    try {
      const res = await authFetch(taskEndpoint(task), { method: "DELETE" });
      if (res.ok) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
        if (selectedTaskId === taskId) setSelectedTaskId(null);
        toast.success("Gelöscht");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Netzwerkfehler"); }
  };

  const linkToEvent = async (task) => {
    // delegate to parent (Terminplaner) to open AppointmentForm with prefill
    if (onLinkToEvent) {
      onLinkToEvent(task, async (createdEventId) => {
        if (createdEventId) await updateTask(task.id, { event_id: createdEventId });
      });
    }
  };

  // Add a new category
  const submitNewCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setSavingNewCat(true);
    try {
      const res = await authFetch(`${API}/task-categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, color: newCatColor }),
      });
      if (res.ok) {
        const created = await res.json();
        setCategories(prev => [...prev, created]);
        setNewCatName("");
        toast.success(`Kategorie „${name}" angelegt`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Netzwerkfehler"); }
    setSavingNewCat(false);
  };

  const updateCategory = async (id, patch) => {
    setCategories(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
    try {
      const res = await authFetch(`${API}/task-categories/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const updated = await res.json();
        setCategories(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c));
      } else {
        loadCategories();
      }
    } catch { loadCategories(); }
  };

  const deleteCategory = async (id) => {
    const cat = categories.find(c => c.id === id);
    if (!cat) return;
    const taskCount = cat.task_count_total || 0;
    const msg = taskCount > 0
      ? `Kategorie „${cat.name}" loeschen? ${taskCount} Aufgabe(n) wechseln auf „Ohne Kategorie".`
      : `Kategorie „${cat.name}" loeschen?`;
    if (!confirm(msg)) return;
    try {
      const res = await authFetch(`${API}/task-categories/${id}`, { method: "DELETE" });
      if (res.ok) {
        setCategories(prev => prev.filter(c => c.id !== id));
        if (selectedListId === `cat:${cat.slug}`) setSelectedListId("all");
        loadTasks();
        toast.success("Geloescht");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Netzwerkfehler"); }
  };

  // ---------- RENDER ----------
  // Sidebar — dynamische Kategorien aus categories[] state
  const renderSidebar = (className = "") => (
    <aside
      className={`flex flex-col gap-1 p-3 overflow-y-auto ${className}`}
      style={{
        background: "var(--tga-bg-card)",
        borderRight: "1px solid var(--tga-border)",
      }}
      data-testid="taskshub-sidebar"
    >
      <p className="text-[10px] uppercase tracking-wider font-bold px-2 pt-1 pb-2"
         style={{ color: "var(--tga-text-dim)" }}>Listen</p>

      {/* Meine Aufgaben */}
      {FIXED_LISTS.map(l => {
        const Active = selectedListId === l.id;
        const Icon = l.icon;
        const count = counts.all;
        return (
          <button key={l.id}
            onClick={() => { setSelectedListId(l.id); setSelectedTaskId(null); }}
            className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-[13px] transition-colors text-left"
            style={{
              background: Active ? "var(--tga-sb-active-bg)" : "transparent",
              color: Active ? "var(--tga-sb-text-active)" : "var(--tga-text-secondary)",
            }}
            onMouseEnter={e => { if (!Active) e.currentTarget.style.background = "var(--tga-bg-hover-soft)"; }}
            onMouseLeave={e => { if (!Active) e.currentTarget.style.background = "transparent"; }}
            data-testid={`taskshub-list-${l.id}`}>
            <span className="flex items-center gap-2 truncate">
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{l.label}</span>
            </span>
            {count > 0 && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                style={{ background: "var(--tga-bg-hover-soft)", color: "var(--tga-text-muted)" }}>
                {count}
              </span>
            )}
          </button>
        );
      })}

      {/* Kategorien Header */}
      <div className="flex items-center justify-between px-2 pt-3 pb-1.5">
        <p className="text-[10px] uppercase tracking-wider font-bold"
           style={{ color: "var(--tga-text-dim)" }}>Kategorien</p>
        <button
          onClick={() => setManageMode(m => !m)}
          className="text-[10px] px-1.5 py-0.5 rounded transition-colors"
          style={{
            color: manageMode ? "var(--tga-sb-text-active)" : "var(--tga-text-muted)",
            background: manageMode ? "var(--tga-bg-hover-soft)" : "transparent",
          }}
          data-testid="taskshub-categories-edit-toggle"
          title="Kategorien bearbeiten"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>

      {/* Dynamische Kategorien */}
      {categories.map(c => {
        const listId = `cat:${c.slug}`;
        const Active = selectedListId === listId;
        const count = counts.bySlug[c.slug] || 0;
        if (manageMode) {
          return (
            <CategoryEditRow key={c.id}
              category={c}
              onRename={(name) => updateCategory(c.id, { name })}
              onColor={(color) => updateCategory(c.id, { color })}
              onDelete={() => deleteCategory(c.id)}
            />
          );
        }
        return (
          <button key={c.id}
            onClick={() => { setSelectedListId(listId); setSelectedTaskId(null); }}
            className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-[13px] transition-colors text-left"
            style={{
              background: Active ? "var(--tga-sb-active-bg)" : "transparent",
              color: Active ? "var(--tga-sb-text-active)" : "var(--tga-text-secondary)",
            }}
            onMouseEnter={e => { if (!Active) e.currentTarget.style.background = "var(--tga-bg-hover-soft)"; }}
            onMouseLeave={e => { if (!Active) e.currentTarget.style.background = "transparent"; }}
            data-testid={`taskshub-list-${c.slug}`}>
            <span className="flex items-center gap-2 truncate">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.color }} />
              <span className="truncate">{c.name}</span>
            </span>
            {count > 0 && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                style={{ background: "var(--tga-bg-hover-soft)", color: "var(--tga-text-muted)" }}>
                {count}
              </span>
            )}
          </button>
        );
      })}

      {/* "+ Kategorie hinzufuegen" */}
      {manageMode ? (
        <div className="px-2 pt-2 pb-1 space-y-1.5" data-testid="taskshub-cat-create-form">
          <input
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitNewCategory(); }}
            placeholder="Name der Kategorie"
            className="w-full px-2 py-1.5 text-[12px] rounded outline-none"
            style={{
              background: "var(--tga-bg-content)",
              border: "1px solid var(--tga-border)",
              color: "var(--tga-text)",
            }}
            data-testid="taskshub-cat-create-name"
          />
          <div className="flex flex-wrap gap-1">
            {CATEGORY_COLOR_PALETTE.map(color => (
              <button key={color}
                onClick={() => setNewCatColor(color)}
                className="w-5 h-5 rounded-full transition-all"
                style={{
                  background: color,
                  outline: newCatColor === color ? "2px solid var(--tga-text-bright)" : "none",
                  outlineOffset: "1px",
                }}
                data-testid={`taskshub-cat-create-color-${color.slice(1)}`}
                title={color}
              />
            ))}
          </div>
          <button
            onClick={submitNewCategory}
            disabled={!newCatName.trim() || savingNewCat}
            className="w-full px-2 py-1.5 rounded text-[12px] font-semibold transition-colors"
            style={{
              background: newCatName.trim() ? "var(--tga-sb-active-bg)" : "var(--tga-bg-content)",
              color: newCatName.trim() ? "var(--tga-sb-text-active)" : "var(--tga-text-muted)",
              cursor: newCatName.trim() ? "pointer" : "not-allowed",
            }}
            data-testid="taskshub-cat-create-submit">
            {savingNewCat ? "Anlegen..." : "+ Kategorie anlegen"}
          </button>
        </div>
      ) : (
        <button
          onClick={() => setManageMode(true)}
          className="flex items-center gap-2 px-2.5 py-1.5 mt-1 rounded-lg text-[12px] transition-colors text-left"
          style={{ color: "var(--tga-text-muted)" }}
          onMouseEnter={(e) => e.currentTarget.style.background = "var(--tga-bg-hover-soft)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
          data-testid="taskshub-cat-add-btn">
          <Plus className="w-3.5 h-3.5" />
          <span>Kategorie hinzufügen</span>
        </button>
      )}

      {projects.length > 0 && (
        <>
          <p className="text-[10px] uppercase tracking-wider font-bold px-2 pt-4 pb-2"
             style={{ color: "var(--tga-text-dim)" }}>Projekte</p>
          {projects.map(p => {
            const listId = `project:${p.id}`;
            const Active = selectedListId === listId;
            const count = counts.byProject[p.id] || 0;
            return (
              <button key={p.id}
                onClick={() => { setSelectedListId(listId); setSelectedTaskId(null); }}
                className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-[13px] transition-colors text-left"
                style={{
                  background: Active ? "var(--tga-sb-active-bg)" : "transparent",
                  color: Active ? "var(--tga-sb-text-active)" : "var(--tga-text-secondary)",
                }}
                onMouseEnter={e => { if (!Active) e.currentTarget.style.background = "var(--tga-bg-hover-soft)"; }}
                onMouseLeave={e => { if (!Active) e.currentTarget.style.background = "transparent"; }}
                data-testid={`taskshub-project-${p.id}`}>
                <span className="flex items-center gap-2 truncate">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "var(--tga-sb-text-active)" }} />
                  <span className="truncate">{p.display_name || p.name || p.title || "Projekt"}</span>
                </span>
                {count > 0 && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                    style={{ background: "var(--tga-bg-hover-soft)", color: "var(--tga-text-muted)" }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </>
      )}
    </aside>
  );

  // List middle
  const renderList = (className = "") => (
    <section className={`flex flex-col overflow-hidden ${className}`}
      style={{ background: "var(--tga-bg-app)" }}
      data-testid="taskshub-list">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3"
           style={{ borderBottom: "1px solid var(--tga-border)" }}>
        <div className="min-w-0">
          <p className="text-[15px] font-bold truncate" style={{ color: "var(--tga-text)" }}>
            {(() => {
              if (selectedListId === "all") return "Meine Aufgaben";
              if (selectedListId.startsWith("cat:")) {
                const slug = selectedListId.slice(4);
                const c = categories.find(x => x.slug === slug);
                return c?.name || "Kategorie";
              }
              if (selectedListId.startsWith("project:")) {
                const pid = selectedListId.slice(8);
                const p = projects.find(x => x.id === pid);
                return p?.display_name || p?.name || p?.title || "Projekt";
              }
              return "Aufgaben";
            })()}
          </p>
          <p className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>
            {filteredTasks.filter(t => t.status !== "erledigt").length} offen · {filteredTasks.filter(t => t.status === "erledigt").length} erledigt
          </p>
        </div>
        <button
          onClick={() => setShowCompleted(s => !s)}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] transition-colors"
          style={{
            color: showCompleted ? "var(--tga-text-bright)" : "var(--tga-text-muted)",
            background: showCompleted ? "var(--tga-bg-hover-soft)" : "transparent",
          }}
          data-testid="taskshub-toggle-completed">
          <Eye className="w-3.5 h-3.5" />
          {showCompleted ? "Erledigte ausblenden" : "Erledigte zeigen"}
        </button>
      </div>

      {/* Quick add */}
      <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--tga-border)" }}>
        <div className="flex items-center gap-2 px-3 h-10 rounded-lg"
             style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)" }}>
          <Plus className="w-4 h-4 flex-shrink-0" style={{ color: "var(--tga-text-muted)" }} />
          <input
            value={quickAdd}
            onChange={e => setQuickAdd(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submitQuick(); }}
            placeholder="Aufgabe hinzufügen — Enter zum Speichern"
            className="flex-1 h-full bg-transparent outline-none text-[13px]"
            style={{ color: "var(--tga-text)" }}
            data-testid="taskshub-quickadd-input"
          />
          {savingQuick && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--tga-text-muted)" }} />}
          {!savingQuick && quickAdd.trim() && (
            <button onClick={submitQuick}
              className="px-2 py-1 rounded text-[11px] font-semibold"
              style={{ background: "var(--tga-sb-active-bg)", color: "var(--tga-sb-text-active)" }}
              data-testid="taskshub-quickadd-save">
              Anlegen
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--tga-text-muted)" }} />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="h-40 flex flex-col items-center justify-center gap-2 text-center px-6">
            <Inbox className="w-8 h-8" style={{ color: "var(--tga-text-dim)" }} />
            <p className="text-[12px]" style={{ color: "var(--tga-text-muted)" }}>
              Keine Aufgaben in dieser Liste.
            </p>
            <p className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>
              Tippe oben in das Eingabefeld und drücke Enter.
            </p>
          </div>
        ) : (
          <ul className="space-y-1" data-testid="taskshub-list-items">
            {filteredTasks.map(t => (
              <TaskRow key={t.id}
                task={t}
                users={users}
                meId={meId}
                selected={selectedTaskId === t.id}
                onSelect={() => { setSelectedTaskId(t.id); setMobileView("detail"); }}
                onToggle={() => toggleDone(t)}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );

  // Detail panel
  const renderDetail = (className = "") => (
    <section className={`flex flex-col overflow-hidden ${className}`}
      style={{
        background: "var(--tga-bg-card)",
        borderLeft: "1px solid var(--tga-border)",
      }}
      data-testid="taskshub-detail">
      {selectedTask ? (
        <TaskDetailPanel
          key={selectedTask.id}
          task={selectedTask}
          projects={projects}
          categories={categories}
          users={users}
          meId={meId}
          onUpdate={(patch) => updateTask(selectedTask.id, patch)}
          onDelete={() => deleteTask(selectedTask.id)}
          onClose={() => { setSelectedTaskId(null); setMobileView("list"); }}
          onLinkToEvent={() => linkToEvent(selectedTask)}
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center px-6">
          <CalendarIcon className="w-8 h-8" style={{ color: "var(--tga-text-dim)" }} />
          <p className="text-[12px]" style={{ color: "var(--tga-text-muted)" }}>
            Wähle eine Aufgabe aus, um Details zu sehen.
          </p>
        </div>
      )}
    </section>
  );

  return (
    <div className="h-full overflow-hidden rounded-lg"
         style={{ border: "1px solid var(--tga-border)" }}
         data-testid="taskshub-root">
      {/* Desktop: 3 columns */}
      <div className="hidden md:grid h-full"
           style={{ gridTemplateColumns: "220px minmax(0, 1fr) minmax(280px, 380px)" }}>
        {renderSidebar()}
        {renderList()}
        {renderDetail()}
      </div>

      {/* Mobile: single screen, toggle list <-> detail */}
      <div className="md:hidden h-full flex flex-col">
        {mobileView === "list" ? (
          <div className="flex-1 overflow-hidden grid grid-rows-[auto_1fr]">
            <div className="flex items-center gap-1 overflow-x-auto px-2 py-2"
                 style={{ background: "var(--tga-bg-card)", borderBottom: "1px solid var(--tga-border)" }}>
              {FIXED_LISTS.map(l => {
                const Active = selectedListId === l.id;
                return (
                  <button key={l.id}
                    onClick={() => { setSelectedListId(l.id); setSelectedTaskId(null); }}
                    className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors"
                    style={{
                      background: Active ? "var(--tga-sb-active-bg)" : "var(--tga-bg-hover-soft)",
                      color: Active ? "var(--tga-sb-text-active)" : "var(--tga-text-secondary)",
                    }}
                    data-testid={`taskshub-mobile-list-${l.id}`}>
                    {l.label}
                  </button>
                );
              })}
              {categories.map(c => {
                const listId = `cat:${c.slug}`;
                const Active = selectedListId === listId;
                return (
                  <button key={c.id}
                    onClick={() => { setSelectedListId(listId); setSelectedTaskId(null); }}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors"
                    style={{
                      background: Active ? "var(--tga-sb-active-bg)" : "var(--tga-bg-hover-soft)",
                      color: Active ? "var(--tga-sb-text-active)" : "var(--tga-text-secondary)",
                    }}
                    data-testid={`taskshub-mobile-list-${c.slug}`}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.color }} />
                    {c.name}
                  </button>
                );
              })}
            </div>
            {renderList("h-full")}
          </div>
        ) : (
          <div className="h-full flex flex-col">
            <div className="flex items-center gap-2 px-3 py-2"
                 style={{ background: "var(--tga-bg-card)", borderBottom: "1px solid var(--tga-border)" }}>
              <button onClick={() => setMobileView("list")}
                className="flex items-center gap-1 px-2 py-1 rounded text-[12px]"
                style={{ color: "var(--tga-sb-text-active)" }}
                data-testid="taskshub-mobile-back">
                <ChevronLeft className="w-4 h-4" /> Zurück
              </button>
            </div>
            {renderDetail("h-full")}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Single task row
// ─────────────────────────────────────────
function TaskRow({ task, selected, onSelect, onToggle, users = [], meId = "" }) {
  const done = task.status === "erledigt";
  const overdue = isOverdue(task);
  const assignedUsers = Array.isArray(task.assigned_users) && task.assigned_users.length
    ? task.assigned_users
    : (task.assigned_to ? [task.assigned_to] : []);
  const showAvatars = assignedUsers.length > 1
    || (assignedUsers.length === 1 && assignedUsers[0] !== meId);
  return (
    <li>
      <div
        onClick={onSelect}
        className="group flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors"
        style={{
          background: selected ? "var(--tga-bg-hover-soft)" : "transparent",
          borderLeft: selected ? "2px solid var(--tga-sb-text-active)" : "2px solid transparent",
        }}
        onMouseEnter={e => { if (!selected) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
        onMouseLeave={e => { if (!selected) e.currentTarget.style.background = "transparent"; }}
        data-testid={`taskshub-row-${task.id}`}
      >
        {/* Checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className="mt-0.5 w-[18px] h-[18px] rounded-full flex-shrink-0 flex items-center justify-center transition-colors"
          style={{
            border: `1.5px solid ${done ? "var(--tga-text-muted)" : "var(--tga-border-strong)"}`,
            background: done ? "var(--tga-text-muted)" : "transparent",
          }}
          data-testid={`taskshub-toggle-${task.id}`}
          aria-label="Aufgabe abhaken"
        >
          {done && <Check className="w-3 h-3" style={{ color: "var(--tga-bg-card)" }} />}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] leading-snug truncate"
             style={{
               color: done ? "var(--tga-text-dim)" : "var(--tga-text)",
               textDecoration: done ? "line-through" : "none",
             }}>
            {task.title}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {task.due_date && (
              <span className="inline-flex items-center gap-1 text-[10px]"
                    style={{ color: overdue ? "#fca5a5" : "var(--tga-text-muted)" }}>
                <CalendarIcon className="w-3 h-3" />
                {fmtDue(task.due_date)}
              </span>
            )}
            {task.project_name && (
              <span className="inline-flex items-center gap-1 text-[10px] truncate max-w-[140px]"
                    style={{ color: "var(--tga-text-muted)" }}>
                <FolderKanban className="w-3 h-3" /> {task.project_name}
              </span>
            )}
            {task.priority && task.priority !== "normal" && (
              <span className="inline-flex items-center gap-1 text-[10px]"
                    style={{ color: PRIORITY_DOT[task.priority] || "var(--tga-text-muted)" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: PRIORITY_DOT[task.priority] || "var(--tga-text-muted)" }} />
                {task.priority === "kritisch" ? "Kritisch" : task.priority === "hoch" ? "Hoch" : "Niedrig"}
              </span>
            )}
            {task.event_id && (
              <span className="inline-flex items-center gap-1 text-[10px]"
                    style={{ color: "var(--tga-sb-text-active)" }}>
                <Link2 className="w-3 h-3" /> Termin
              </span>
            )}
          </div>
        </div>

        {/* Assignee avatars (only when there are others or it's a team task) */}
        {showAvatars && (
          <div className="flex -space-x-1.5 flex-shrink-0 mt-0.5"
               data-testid={`taskshub-row-avatars-${task.id}`}>
            {assignedUsers.slice(0, 3).map(uid => {
              const u = users.find(x => x.id === uid);
              const initials = (u?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
              return (
                <span key={uid}
                  title={u?.name || uid}
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold"
                  style={{
                    background: "var(--tga-sb-active-bg)",
                    color: "var(--tga-sb-text-active)",
                    boxShadow: "0 0 0 1.5px var(--tga-bg-app)",
                  }}>
                  {initials}
                </span>
              );
            })}
            {assignedUsers.length > 3 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[8px] font-bold"
                    style={{
                      background: "var(--tga-bg-hover-soft)",
                      color: "var(--tga-text-muted)",
                      boxShadow: "0 0 0 1.5px var(--tga-bg-app)",
                    }}>
                +{assignedUsers.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

// ─────────────────────────────────────────
// Stable text fields (BUGFIX: prevents character loss + cursor jumps when parent re-renders or
// server response arrives mid-typing). Local state, sync only when not focused, commit on
// onBlur + 1500ms idle.
// ─────────────────────────────────────────
function StableTitle({ value, onCommit, ...rest }) {
  const [local, setLocal] = useState(value || "");
  const focusedRef = useRef(false);
  const timer = useRef(null);
  useEffect(() => { if (!focusedRef.current) setLocal(value || ""); }, [value]);
  const schedule = (next) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (next !== (value || "")) onCommit(next); }, 1500);
  };
  return (
    <textarea
      {...rest}
      value={local}
      onFocus={(e) => { focusedRef.current = true; rest.onFocus?.(e); }}
      onChange={(e) => { setLocal(e.target.value); schedule(e.target.value); }}
      onBlur={(e) => {
        focusedRef.current = false;
        if (timer.current) { clearTimeout(timer.current); timer.current = null; }
        if (local !== (value || "")) onCommit(local);
        rest.onBlur?.(e);
      }}
    />
  );
}

// ─────────────────────────────────────────
// Detail panel
// ─────────────────────────────────────────
function TaskDetailPanel({ task, projects, categories = [], users, meId, onUpdate, onDelete, onClose, onLinkToEvent }) {
  const [dueDate, setDueDate] = useState(task.due_date ? task.due_date.slice(0, 10) : "");
  const [category, setCategory] = useState(task.category || "");
  const [projectId, setProjectId] = useState(task.project_id || "");
  const [priority, setPriority] = useState(task.priority || "normal");
  const [visibility, setVisibility] = useState(task.visibility || "privat");
  const [assignedUsers, setAssignedUsers] = useState(
    Array.isArray(task.assigned_users) && task.assigned_users.length
      ? task.assigned_users
      : (task.assigned_to ? [task.assigned_to] : [])
  );
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const assigneeRef = useRef(null);

  // Sync external changes when switching to a different task
  useEffect(() => {
    setDueDate(task.due_date ? task.due_date.slice(0, 10) : "");
    setCategory(task.category || "");
    setProjectId(task.project_id || "");
    setPriority(task.priority || "normal");
    setVisibility(task.visibility || "privat");
    setAssignedUsers(
      Array.isArray(task.assigned_users) && task.assigned_users.length
        ? task.assigned_users
        : (task.assigned_to ? [task.assigned_to] : [])
    );
  }, [task.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close assignee dropdown on outside click
  useEffect(() => {
    if (!assigneeOpen) return;
    const handler = (e) => {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target)) setAssigneeOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [assigneeOpen]);

  const inp = "w-full px-3 py-2 text-[13px] rounded-lg outline-none transition-colors";
  const inpStyle = {
    background: "var(--tga-bg-content)",
    border: "1px solid var(--tga-border)",
    color: "var(--tga-text)",
  };
  const labelCls = "text-[10px] uppercase tracking-wider font-bold block mb-1.5";
  const labelStyle = { color: "var(--tga-text-dim)" };

  const done = task.status === "erledigt";

  const toggleAssignee = (uid) => {
    const next = assignedUsers.includes(uid)
      ? assignedUsers.filter(x => x !== uid)
      : [...assignedUsers, uid];
    setAssignedUsers(next);
    onUpdate({ assigned_users: next });
  };

  const assignedNames = assignedUsers
    .map(uid => users.find(u => u.id === uid)?.name || "")
    .filter(Boolean);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3"
           style={{ borderBottom: "1px solid var(--tga-border)" }}>
        <p className="text-[12px] uppercase tracking-wider font-bold"
           style={{ color: "var(--tga-text-dim)" }}>Details</p>
        <div className="flex items-center gap-1">
          <button onClick={onDelete}
            className="p-1.5 rounded hover:bg-red-500/10 transition-colors"
            data-testid="taskshub-detail-delete"
            title="Löschen">
            <Trash2 className="w-4 h-4" style={{ color: "#fca5a5" }} />
          </button>
          <button onClick={onClose}
            className="p-1.5 rounded transition-colors"
            style={{ color: "var(--tga-text-muted)" }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--tga-bg-hover-soft)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            data-testid="taskshub-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Title */}
        <div className="flex items-start gap-2">
          <button
            onClick={() => onUpdate({ status: done ? "offen" : "erledigt" })}
            className="mt-1 w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center transition-colors"
            style={{
              border: `1.5px solid ${done ? "var(--tga-text-muted)" : "var(--tga-border-strong)"}`,
              background: done ? "var(--tga-text-muted)" : "transparent",
            }}
            data-testid="taskshub-detail-toggle"
          >
            {done && <Check className="w-3 h-3" style={{ color: "var(--tga-bg-card)" }} />}
          </button>
          <StableTitle
            key={`title-${task.id}`}
            value={task.title || ""}
            onCommit={(v) => onUpdate({ title: v })}
            rows={1}
            className="flex-1 bg-transparent outline-none text-[15px] font-semibold resize-none"
            style={{
              color: done ? "var(--tga-text-dim)" : "var(--tga-text-bright)",
              textDecoration: done ? "line-through" : "none",
            }}
            data-testid="taskshub-detail-title"
          />
        </div>

        {/* Description */}
        <div>
          <label className={labelCls} style={labelStyle}>Notiz</label>
          <StableTitle
            key={`desc-${task.id}`}
            value={task.description || ""}
            onCommit={(v) => onUpdate({ description: v })}
            placeholder="Beschreibung hinzufügen…"
            rows={3}
            className={inp + " resize-none"}
            style={inpStyle}
            data-testid="taskshub-detail-description"
          />
        </div>

        {/* Due date + Priority */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} style={labelStyle}>Fälligkeit</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                onUpdate({ due_date: e.target.value ? `${e.target.value}T08:00:00` : null });
              }}
              className={inp}
              style={inpStyle}
              data-testid="taskshub-detail-duedate"
            />
          </div>
          <div>
            <label className={labelCls} style={labelStyle}>Priorität</label>
            <select
              value={priority}
              onChange={(e) => { setPriority(e.target.value); onUpdate({ priority: e.target.value }); }}
              className={inp}
              style={inpStyle}
              data-testid="taskshub-detail-priority">
              <option value="niedrig">Niedrig</option>
              <option value="normal">Normal</option>
              <option value="hoch">Hoch</option>
              <option value="kritisch">Kritisch</option>
            </select>
          </div>
        </div>

        {/* Category + Project */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} style={labelStyle}>Kategorie</label>
            <select
              value={category || ""}
              onChange={(e) => { setCategory(e.target.value); onUpdate({ category: e.target.value || null }); }}
              className={inp}
              style={inpStyle}
              data-testid="taskshub-detail-category">
              <option value="">– Ohne Kategorie –</option>
              {categories.map(c => (
                <option key={c.id} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} style={labelStyle}>Projekt</label>
            <select
              value={projectId}
              onChange={(e) => { setProjectId(e.target.value); onUpdate({ project_id: e.target.value || null }); }}
              className={inp}
              style={inpStyle}
              data-testid="taskshub-detail-project">
              <option value="">– Kein Projekt –</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.display_name || p.name || p.title}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Assigned to (multi) */}
        <div ref={assigneeRef} className="relative">
          <label className={labelCls} style={labelStyle}>
            Zugewiesen an {assignedUsers.length > 0 && <span style={{ color: "var(--tga-text-muted)" }}>({assignedUsers.length})</span>}
          </label>
          <button
            onClick={() => setAssigneeOpen(o => !o)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-left"
            style={inpStyle}
            data-testid="taskshub-detail-assignee">
            {assignedUsers.length === 0 ? (
              <span style={{ color: "var(--tga-text-dim)" }}>– Niemand –</span>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                <div className="flex -space-x-1.5">
                  {assignedUsers.slice(0, 4).map(uid => {
                    const u = users.find(x => x.id === uid);
                    const initials = (u?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
                    return (
                      <span key={uid}
                        title={u?.name || uid}
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ring-2"
                        style={{
                          background: "var(--tga-sb-active-bg)",
                          color: "var(--tga-sb-text-active)",
                          ringColor: "var(--tga-bg-content)",
                          boxShadow: "0 0 0 2px var(--tga-bg-content)",
                        }}>
                        {initials}
                      </span>
                    );
                  })}
                  {assignedUsers.length > 4 && (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[9px] font-bold"
                          style={{ background: "var(--tga-bg-hover-soft)", color: "var(--tga-text-muted)" }}>
                      +{assignedUsers.length - 4}
                    </span>
                  )}
                </div>
                <span className="truncate" style={{ color: "var(--tga-text-secondary)" }}>
                  {assignedNames.slice(0, 2).join(", ")}
                  {assignedNames.length > 2 && ` +${assignedNames.length - 2}`}
                </span>
              </div>
            )}
          </button>
          {assigneeOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg z-30"
                 style={{ background: "var(--tga-bg-elevated)", border: "1px solid var(--tga-border-strong)" }}
                 data-testid="taskshub-detail-assignee-dropdown">
              {users.map(u => {
                const checked = assignedUsers.includes(u.id);
                return (
                  <label key={u.id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                    style={{ borderBottom: "1px solid var(--tga-border)" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "var(--tga-bg-hover-soft)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    data-testid={`taskshub-detail-assignee-${u.id}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAssignee(u.id)}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: "var(--tga-sb-text-active)" }}
                    />
                    <span className="text-[12px]" style={{ color: "var(--tga-text)" }}>
                      {u.name}{u.id === meId ? " (Ich)" : ""}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Visibility */}
        <div>
          <label className={labelCls} style={labelStyle}>Sichtbarkeit</label>
          <div className="grid grid-cols-2 gap-1.5">
            {VISIBILITY_OPTS.map(v => {
              const on = visibility === v.k;
              return (
                <button key={v.k}
                  onClick={() => { setVisibility(v.k); onUpdate({ visibility: v.k }); }}
                  className="px-2 py-1.5 rounded text-[11px] font-semibold transition-colors"
                  style={{
                    background: on ? "var(--tga-sb-active-bg)" : "var(--tga-bg-content)",
                    color: on ? "var(--tga-sb-text-active)" : "var(--tga-text-secondary)",
                    border: `1px solid ${on ? "var(--tga-border-focus)" : "var(--tga-border)"}`,
                  }}
                  data-testid={`taskshub-detail-vis-${v.k}`}>
                  {v.l}
                </button>
              );
            })}
          </div>
        </div>

        {/* Link to event */}
        <div className="pt-2" style={{ borderTop: "1px solid var(--tga-border)" }}>
          <button
            onClick={onLinkToEvent}
            className="w-full flex items-center justify-center gap-2 h-10 rounded-lg text-[12px] font-semibold transition-colors"
            style={{
              background: task.event_id ? "var(--tga-sb-active-bg)" : "var(--tga-bg-content)",
              color: task.event_id ? "var(--tga-sb-text-active)" : "var(--tga-text)",
              border: `1px solid ${task.event_id ? "var(--tga-border-focus)" : "var(--tga-border)"}`,
            }}
            data-testid="taskshub-detail-link-event">
            <Link2 className="w-4 h-4" />
            {task.event_id ? "Termin verknüpft – neuen anlegen" : "Zum Termin hinzufügen"}
          </button>
        </div>
      </div>

      {/* Footer meta */}
      <div className="px-4 py-2 text-[10px] flex items-center justify-between"
           style={{ borderTop: "1px solid var(--tga-border)", color: "var(--tga-text-dim)" }}>
        <span>
          {task.created_at ? `Erstellt: ${format(parseISO(task.created_at), "dd.MM.yyyy HH:mm")}` : ""}
        </span>
        {task.updated_at && task.updated_at !== task.created_at && (
          <span>Aktualisiert: {format(parseISO(task.updated_at), "dd.MM.yyyy HH:mm")}</span>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────
// CategoryEditRow — Inline-Bearbeitung einer Kategorie in der Sidebar (manage mode)
// Zeigt Farb-Picker, editierbaren Namen, Loesch-Button. Defaults sind nicht loeschbar.
// ─────────────────────────────────────────
function CategoryEditRow({ category, onRename, onColor, onDelete }) {
  const [name, setName] = useState(category.name);
  const [colorOpen, setColorOpen] = useState(false);
  const colorRef = useRef(null);

  useEffect(() => { setName(category.name); }, [category.name]);

  useEffect(() => {
    if (!colorOpen) return;
    const handler = (e) => {
      if (colorRef.current && !colorRef.current.contains(e.target)) setColorOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colorOpen]);

  const commitName = () => {
    const v = name.trim();
    if (v && v !== category.name) onRename(v);
  };

  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg"
         style={{ background: "var(--tga-bg-hover-soft)" }}
         data-testid={`taskshub-cat-edit-${category.slug}`}>
      {/* Color */}
      <div ref={colorRef} className="relative flex-shrink-0">
        <button
          onClick={() => setColorOpen(o => !o)}
          className="w-4 h-4 rounded-full transition-transform hover:scale-110"
          style={{ background: category.color, border: "2px solid var(--tga-bg-card)" }}
          data-testid={`taskshub-cat-color-btn-${category.slug}`}
          title="Farbe ändern"
        />
        {colorOpen && (
          <div className="absolute top-full left-0 mt-1 p-2 rounded-lg shadow-lg z-30 grid grid-cols-5 gap-1"
               style={{ background: "var(--tga-bg-elevated)", border: "1px solid var(--tga-border-strong)" }}>
            {CATEGORY_COLOR_PALETTE.map(color => (
              <button key={color}
                onClick={() => { onColor(color); setColorOpen(false); }}
                className="w-5 h-5 rounded-full transition-transform hover:scale-125"
                style={{ background: color, outline: category.color === color ? "2px solid var(--tga-text-bright)" : "none" }}
                data-testid={`taskshub-cat-color-${category.slug}-${color.slice(1)}`} />
            ))}
          </div>
        )}
      </div>

      {/* Name */}
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitName}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setName(category.name); e.currentTarget.blur(); } }}
        className="flex-1 min-w-0 px-1.5 py-0.5 text-[12px] rounded outline-none"
        style={{ background: "transparent", color: "var(--tga-text)", border: "1px solid var(--tga-border)" }}
        data-testid={`taskshub-cat-name-${category.slug}`}
      />

      {/* Delete */}
      <button
        onClick={onDelete}
        className="p-1 rounded transition-colors flex-shrink-0"
        style={{ color: "var(--tga-text-muted)" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.15)"; e.currentTarget.style.color = "#fca5a5"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--tga-text-muted)"; }}
        title="Kategorie löschen"
        data-testid={`taskshub-cat-delete-${category.slug}`}>
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}
